import { differenceInDays } from 'date-fns';

import { isMobileUserAgent, shouldShowMobileWeb } from '@confluence/mobile-detection';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { useUserByLicensedProducts } from '@confluence/onboarding-helpers/entry-points/hooks/useUserByLicensedProducts';
import { useIsNav4Enabled } from '@confluence/nav4-enabled';
import { fg } from '@confluence/feature-gating';
import { ENHANCE_ORIENTATION_TOUR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';

/**
 * Eligibility hook for enhance tour experiment with following requirements to be eligible
 * Desktop only
 * First 7 days of onboarding
 * Enrollled in Statsig experiment
 * Return seperate values for land versus xflow, as well as onboarding state values for tourSeenAt and hasDismissedTour
 */
export const useIsEligibleForEnhanceOrientationTour = () => {
	const isNav4Enabled = useIsNav4Enabled();

	// Onboarding state to check when they've seen the tour
	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState([...Object.values(ENHANCE_ORIENTATION_TOUR_ONBOARDING_STATE_KEYS)]);

	const isDesktopUser = !(isMobileUserAgent() || shouldShowMobileWeb());

	const { tourSeenAt, hasDismissedTour, hasSeenFinishNudge, hasCompletedTour } =
		deserializeState(onboardingStateData);

	const isInOnboardingPeriod =
		Boolean(tourSeenAt) && differenceInDays(new Date(), new Date(tourSeenAt)) <= 7;
	const tourExpired = Boolean(tourSeenAt) && differenceInDays(new Date(), new Date(tourSeenAt)) > 7;

	const potentiallyEligibleUserForTour = !tourExpired && isDesktopUser && isNav4Enabled;

	const {
		isLandUser,
		isXflowUser,
		error: userTypeError,
		loading: userTypeLoading,
	} = useUserByLicensedProducts({
		skip: !potentiallyEligibleUserForTour || !fg('cc-onboarding-enhance-orientation-tour-eligible'),
	});

	if (userTypeLoading || onboardingStateLoading) {
		return {
			isEligibleForLand: false,
			isEligibleForXflow: false,
			loading: true,
			error: false,
			hasDismissedTour,
			hasSeenFinishNudge,
			hasCompletedTour,
			isXflowUser,
		};
	}

	if (userTypeError || onboardingStateError) {
		return {
			isEligibleForLand: false,
			isEligibleForXflow: false,
			loading: false,
			error: userTypeError || onboardingStateError,
			hasDismissedTour,
			hasSeenFinishNudge,
			hasCompletedTour,
			isXflowUser,
		};
	}

	return {
		isEligibleForLand: isLandUser && (tourSeenAt === undefined || isInOnboardingPeriod),
		isEligibleForXflow: isXflowUser && (tourSeenAt === undefined || isInOnboardingPeriod),
		loading: false,
		error: false,
		hasDismissedTour,
		hasCompletedTour,
		hasSeenFinishNudge,
		tourSeenAt,
		isXflowUser,
	};
};
